import { useCallback, useEffect, useRef, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { Box, Button, Card, Typography, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { useFormContext, useWatch } from 'react-hook-form';

import { GET_PROPERTY_TYPES } from '@modules/_shared/common/queries';
import type { TransactionFormData } from '@modules/_shared/components/create-transaction/hooks/useTransactionStepper';
import { RaAddressInput } from '@modules/_shared/components/form/RaAddressInput';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import ScraperListingCard from '@modules/_shared/components/scraper-listing-card/ScraperListingCard';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useAgencyFilter } from '@modules/_shared/hooks/useAgencyFilter';
import { useAutoScrollResultIntoView } from '@modules/_shared/hooks/useAutoScrollResultIntoView';
import { useDuplicateTransactions } from '@modules/_shared/hooks/useDuplicateTransactions';
import { useExcludedListingIds } from '@modules/_shared/hooks/useExcludedListingIds';
import {
  hasAllRequiredAddressFields,
  useListingFormPrefill,
} from '@modules/_shared/hooks/useListingFormPrefill';
import { useListingsSearch } from '@modules/_shared/hooks/useListingsSearch';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { PropertyTypesProvider } from '@modules/_shared/providers/PropertyTypesProvider';
import { formatAddress } from '@modules/_shared/utils/formatting';
import {
  type ListingItem,
  listingToAddress,
} from '@modules/_shared/utils/listingDataTransformers';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

import {
  useTransactionFormUi,
  useTransactionGridSpacing,
} from '../TransactionFormUi';
import { TransactionStepShell } from '../TransactionStepShell';

type AddressStepProps = {
  onNext: () => void;
  setNextLabel: (label: string | undefined) => void;
  trackListingSelected: (listingId: number) => void;
  flowId: string;
  onLoadingStateChange?: (
    isLoading: boolean,
    searchStartTime: number | null,
  ) => void;
};

export const AddressStep: React.FC<AddressStepProps> = ({
  onNext,
  setNextLabel,
  trackListingSelected,
  flowId,
  onLoadingStateChange,
}) => {
  const { t, countryCode, locale } = useLocale();
  const isOnboarding = useTransactionFormUi() === 'onboarding';
  const gridSpacing = useTransactionGridSpacing();
  const { me } = useAppData();
  const currency = getCurrencyByCountryCode(countryCode ?? 'CH');
  const { setValue, getValues } = useFormContext<TransactionFormData>();
  const address = useWatch<TransactionFormData, 'address'>({ name: 'address' });
  const containerRef = useRef<HTMLDivElement>(null);
  const searchStartScrollYRef = useRef<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { data: propertyTypesData } = useQuery(GET_PROPERTY_TYPES);

  const agencyFilter = useAgencyFilter(me?.teams);
  const teamIds = me?.teams?.map(t => t.team_id);
  const { excludedIds } = useExcludedListingIds(teamIds);
  const {
    listings,
    loading,
    error: searchError,
  } = useListingsSearch(address, countryCode, agencyFilter, excludedIds);

  const { transactions: duplicateTransactions } =
    useDuplicateTransactions(address);

  const { track } = useAnalytics();

  const [userDismissedDuplicateWarning, setUserDismissedDuplicateWarning] =
    useState(false);
  const showDuplicateModal =
    duplicateTransactions.length > 0 && !userDismissedDuplicateWarning;

  const sentDuplicateEventRef = useRef(false);
  const lastSelectedListingIdRef = useRef<number | null>(null);
  const listingsSearchStartTimeRef = useRef<number | null>(null);
  const lastSearchCompletedRef = useRef(false);

  useEffect(() => {
    if (showDuplicateModal && !sentDuplicateEventRef.current) {
      track(ANALYTICS_EVENTS.TRANSACTION_CREATION_DUPLICATE_WARNING_SHOWN, {
        flow_id: flowId,
        duplicate_count: duplicateTransactions.length,
        transaction_ids: duplicateTransactions.map(t => t.id),
      });
      sentDuplicateEventRef.current = true;
    } else if (!showDuplicateModal) {
      // Reset when modal is dismissed
      sentDuplicateEventRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDuplicateModal, flowId, duplicateTransactions.length]);

  // Track listings search API latency
  useEffect(() => {
    if (loading) {
      // Query started - record start time if not already tracking
      if (listingsSearchStartTimeRef.current == null) {
        listingsSearchStartTimeRef.current = Date.now();
        lastSearchCompletedRef.current = false;
        searchStartScrollYRef.current =
          typeof window !== 'undefined' ? window.scrollY : 0;
      }
      return;
    }

    // Query completed - calculate duration and fire event
    if (
      listingsSearchStartTimeRef.current != null &&
      !lastSearchCompletedRef.current
    ) {
      const duration_ms = Date.now() - listingsSearchStartTimeRef.current;
      track(ANALYTICS_EVENTS.TRANSACTION_CREATION_LISTINGS_SEARCH_COMPLETED, {
        flow_id: flowId,
        duration_ms,
        results_count: listings.length,
      });
      listingsSearchStartTimeRef.current = null;
      lastSearchCompletedRef.current = true;
    }
  }, [loading, listings.length, track, flowId]);

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingStateChange?.(loading, listingsSearchStartTimeRef.current);
  }, [loading, onLoadingStateChange]);

  const [dismissedError, setDismissedError] = useState<Error | null>(null);
  const error = searchError === dismissedError ? null : searchError;

  const hasListings = listings.length > 0;

  const addressKey = `${address?.route}-${address?.street_number}-${address?.postcode}`;
  const canScrollToListings = useCallback(() => {
    const startY = searchStartScrollYRef.current;
    const currentY = typeof window !== 'undefined' ? window.scrollY : 0;
    return startY == null || Math.abs(currentY - startY) <= 50;
  }, []);
  const listingsHeadingRef = useAutoScrollResultIntoView<HTMLDivElement>({
    isActive: isOnboarding && isMobile && !loading && hasListings,
    resultKey: addressKey,
    canScroll: canScrollToListings,
  });

  const currentNextLabel = hasListings
    ? isOnboarding
      ? t('Enter manually')
      : t('No, I sold a different property')
    : undefined;

  const prevNextLabelRef = useRef<string | undefined>(undefined);
  if (prevNextLabelRef.current !== currentNextLabel) {
    prevNextLabelRef.current = currentNextLabel;
    setNextLabel(currentNextLabel);
  }

  const prefillFromListing = useListingFormPrefill(
    setValue,
    propertyTypesData?.property_types,
  );

  const handleSelectListing = (listing: ListingItem) => {
    const listingAddress = listingToAddress(listing);
    const currentAddress = getValues('address');

    // Only track if it's a different listing than the last one selected
    if (lastSelectedListingIdRef.current !== listing.id) {
      trackListingSelected(listing.id);
      lastSelectedListingIdRef.current = listing.id;
    }

    if (!hasAllRequiredAddressFields(currentAddress, listingAddress)) {
      // Trigger native form validation
      containerRef.current?.closest('form')?.requestSubmit();
      return;
    }

    prefillFromListing(listing);
    onNext();
  };

  const handleDismissDuplicateWarning = () => {
    setUserDismissedDuplicateWarning(true);
  };

  const prevAddressKeyRef = useRef(addressKey);
  if (prevAddressKeyRef.current !== addressKey) {
    prevAddressKeyRef.current = addressKey;
    if (userDismissedDuplicateWarning) {
      setUserDismissedDuplicateWarning(false);
    }
  }

  const addressBody = (
    <Grid
      container
      sx={{
        gap: gridSpacing,
        alignContent: 'flex-start',
        ...(isOnboarding ? undefined : { p: 2 }),
      }}
      ref={containerRef}
    >
      <Grid size={12}>
        <RaAddressInput
          id="property_address"
          countryRestriction={countryCode}
          setValue={setValue}
          formValues={address ?? {}}
          path="address"
          uiVariant={isOnboarding ? 'onboarding' : undefined}
          requiredFields={{
            street_number: true,
            route: true,
            country_code: !isOnboarding,
            country: true,
            locality: true,
            postcode: true,
            state: true,
          }}
          debounceGeocodeMs={500}
        />
      </Grid>

      <Grid container spacing={2} sx={{ width: '100%' }}>
        {loading ? (
          <Grid
            size={12}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              gap: 2,
            }}
          >
            <LoadingSpinner sx={{ p: 0 }} />
            <Typography variant="body2" color="text.secondary">
              {t('Searching for listings at this address...')}
            </Typography>
          </Grid>
        ) : hasListings ? (
          <PropertyTypesProvider>
            <Grid
              size={12}
              ref={listingsHeadingRef}
              sx={
                isOnboarding
                  ? { scrollMarginTop: 24, scrollMarginBottom: 120 }
                  : undefined
              }
            >
              <Typography
                variant={isOnboarding ? 'subtitle2' : 'h6'}
                sx={{
                  fontWeight: 'bold',
                  fontSize: isOnboarding ? 14 : undefined,
                  color: isOnboarding ? 'text.primary' : undefined,
                }}
              >
                {t('Did you sell any of these properties?')}
              </Typography>
            </Grid>
            {listings.map(listing => (
              <Grid
                key={listing.id}
                size={{ xs: 12, sm: isOnboarding ? 6 : 4 }}
              >
                <Card
                  elevation={0}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    flex: 1,
                    overflow: 'hidden',
                    height: '100%',
                    border: '1px solid',
                    borderColor: isOnboarding ? 'grey.200' : 'divider',
                    borderRadius: isOnboarding ? '12px' : 1,
                  }}
                >
                  <ScraperListingCard
                    listing={listing}
                    variant="vertical"
                    showAgencyName
                    showOfflineSince
                    action={
                      <Button
                        variant="contained"
                        fullWidth
                        size="small"
                        onClick={() => handleSelectListing(listing)}
                        sx={
                          isOnboarding
                            ? {
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '8px',
                              }
                            : undefined
                        }
                      >
                        {isOnboarding
                          ? t('I sold this one')
                          : t('Select listing')}
                      </Button>
                    }
                  />
                </Card>
              </Grid>
            ))}
          </PropertyTypesProvider>
        ) : null}
      </Grid>
    </Grid>
  );

  return (
    <>
      <TransactionStepShell title={t('Address')} drawerPadding={false}>
        {addressBody}
      </TransactionStepShell>

      <MutationErrorSnackbar
        error={error}
        onClose={() => setDismissedError(searchError)}
      />
      <SlideDialog
        open={showDuplicateModal}
        onClose={handleDismissDuplicateWarning}
        title={t('Existing transaction found')}
        cancelText={t('Cancel')}
        saveIcon={null}
        saveText={t('Continue')}
        onSave={handleDismissDuplicateWarning}
      >
        <Box
          sx={{
            p: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              marginBottom: '16px',
            }}
          >
            {t('We found an existing transaction for this address')}
          </Typography>
          {duplicateTransactions.map(transaction => (
            <div key={transaction.id} style={{ marginTop: 16 }}>
              <Typography variant="subtitle2">
                {formatAddress(transaction.property ?? {}, ', ')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                }}
              >
                {t('Price')}:{' '}
                {transaction.purchase_price
                  ? formatPrice(transaction.purchase_price, locale, currency)
                  : '-'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                }}
              >
                {t('Agent')}: {transaction.seller_broker?.first_name}{' '}
                {transaction.seller_broker?.last_name}
              </Typography>
            </div>
          ))}
          <Typography
            variant="body1"
            sx={{
              mt: 2,
            }}
          >
            {t('Do you want to continue anyway?')}
          </Typography>
        </Box>
      </SlideDialog>
    </>
  );
};
